/**
 * Toggle
 *
 * Features:
 * 	- a11y toggle
 * 	- disable / enable on breakpoint (optional)
 * 	- Show / Hide on hover (optional)
 *  - Listener for other toggles
 *
 */

import AccessibleToggle from '../vendor/a11y-toggle';
import breakpoints from '../ui/breakpoints';

class Toggle {
	constructor(elem, settings = {}) {
		this.elem = elem;
		if (!this.elem) {
			return;
		}

		this.toggle = null;
		this.toggleEnabled = false;
		this.hoverTimer = null;

		// Default settings
		this.settings = {
			activeClass: 'a-toggle--open',
			toggleOnHover: false, // Toggle element on hover (in addition to click)
			hoverContainers: [this.elem], // Which Dom Node to use for hover events
			hoverTimeout: 200, // How long timeout to close
			useBreakpoints: false, // Enable / disable based on breakpoint
			breakpointType: 'general', // general or precise
			enabledBreakpoints: ['desktop'], // List of breakpoints where toggle is enabled
			enableCheck: null, // Custom function to see if toggle can be enabled
			onShow: null, // function callback for on show
			onHide: null, // function callback for on hide
			onEnable: null, // function callback for on enable
			onDisable: null, // function callback for on disable
			autoClose: false, // Close automatically when some other popup is opened
			autoCloseParent: document.body
		};

		this.triggers = document.querySelectorAll('[data-toggle="'+this.elem.id+'"]');
		if (this.triggers) {
			this.settings.hoverContainers.push(...this.triggers);
		}

		// Ovverride default settings
		for (let key of Object.keys(settings)) {
			if (typeof this.settings[key] !== 'undefined') {
				this.settings[key] = settings[key];
			}
		}

		// Bind this to class context
		this.onShow = this.onShow.bind(this);
		this.onHide = this.onHide.bind(this);

		// Accessible toggle plugin settings
		this.pluginSettings = {
			isDynamic: true,
			onShow: this.onShow,
			onHide: this.onHide
		};

		if (typeof this.settings.onEnable === 'function') {
			this.pluginSettings.onEnable = (() => this.settings.onEnable(this));
		}

		if (typeof this.settings.onDisable === 'function') {
			this.pluginSettings.onDisable = (() => this.settings.onDisable(this));
		}

		// Check if toggle needs to be enabled
		if (!this.settings.useBreakpoints || (this.settings.useBreakpoints && this.settings.enabledBreakpoints.includes(breakpoints.getBreakpoint(this.settings.breakpointType)))) {
			this.enable();
		}

		if (this.settings.useBreakpoints) {
			// Add subscriber to call when breakpoint changes between breakpoints
			breakpoints.addChangeSubscriber((breakpoint) => {
				if (this.settings.enabledBreakpoints.includes(breakpoint)) {
					this.enable();
				} else {
					this.disable();
				}
			}, this.settings.breakpointType);
		}
	}

	onShow() {
		this.elem.classList.add(this.settings.activeClass);

		if (typeof this.settings.onShow === 'function') {
			this.settings.onShow(this);
		}
	}

	onHide() {
		this.elem.classList.remove(this.settings.activeClass);

		if (typeof this.settings.onHide === 'function') {
			this.settings.onHide(this);
		}
	}

	onMouseEnter() {
		clearTimeout(this.hoverTimer);
		this.show();
	}

	onMouseLeave() {
		this.hoverTimer = setTimeout(() => {
			this.hide();
		}, this.settings.hoverTimeout);
	}

	onAutoClose(e) {
		if (e.target === this.elem) {
			return;
		}

		clearTimeout(this.hoverTimer);
		this.toggle.hide();
	}

	/**
	 * PUBLIC functions to use
	 */

	enable() {
		if (this.toggleEnabled) {
			return;
		}

		// Double check correct breakpoint
		if (this.settings.useBreakpoints && !this.settings.enabledBreakpoints.includes(breakpoints.getBreakpoint(this.settings.breakpointType))) {
			return;
		}

		if (typeof this.settings.enableCheck === 'function') {
			if (!this.settings.enableCheck(this)) {
				return; // Cannot
			}
		}

		this.toggleEnabled = true;

		if (this.settings.toggleOnHover) {
			// Bind this to class context
			this.onMouseEnter = this.onMouseEnter.bind(this);
			this.onMouseLeave = this.onMouseLeave.bind(this);

			// On mouse enter show toggle
			for (let item of this.settings.hoverContainers) {
				item.addEventListener('mouseenter', this.onMouseEnter);

				// On mouse leave hide toggle
				item.addEventListener('mouseleave', this.onMouseLeave);
			}
		}

		if (this.settings.autoClose) {
			this.onAutoClose = this.onAutoClose.bind(this);
			this.settings.autoCloseParent.addEventListener('toggle-show', this.onAutoClose);
		}

		this.toggle = new AccessibleToggle(this.elem, this.pluginSettings);
	}

	disable() {
		if (!this.toggleEnabled) {
			return;
		}

		this.toggleEnabled = false;

		if (this.settings.toggleOnHover) {
			for (let item of this.settings.hoverContainers) {
				item.removeEventListener('mouseenter', this.onMouseEnter);
				item.removeEventListener('mouseleave', this.onMouseLeave);
			}
		}

		if (this.settings.autoClose) {
			this.settings.autoCloseParent.removeEventListener('toggle-show', this.onAutoClose);
		}

		if (typeof this.toggle !== 'undefined' && this.toggle.destroy) {
			this.toggle.hide();
			this.toggle.destroy();
		}
	}

	show() {
		this.toggle.show();
	}

	hide() {
		this.toggle.hide();
	}
}

export { Toggle };
